import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API from "@/lib/api";
import { getCaseProgress } from "@/lib/caseProgress";
import { Check, Circle, Clock, CheckCircle, Warning } from "@phosphor-icons/react";

export default function ReferrerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.get(`/referrer-portal/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center" data-testid="referrer-portal-error">
        <Warning weight="fill" className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500 font-medium">{error || "Data not found"}</p>
      </div>
    );
  }

  const { case: caseData, referrer_name, client_name } = data;
  const stages = caseData.stages || [];
  const completedCount = stages.filter((s) => s.status === "completed").length;
  const progress = getCaseProgress(stages);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="referrer-portal-page">
      <div style={{ background: '#100f5d' }}>
        <div className="h-[3px]" style={{ background: '#c87d3a' }} />
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-10 w-auto" />
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Case Progress
              </h1>
              <p className="text-xs text-slate-300">Referrer portal for {referrer_name}</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{progress}%</span>
            <p className="text-xs" style={{ color: '#e89e63' }}>complete</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Case summary */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 font-medium">Property</p>
              <p className="text-sm font-semibold text-slate-900">{caseData.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{caseData.property_address}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Client</p>
              <p className="text-sm font-semibold text-slate-900">{client_name}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Transaction</p>
              <p className="text-sm font-medium text-slate-700 capitalize">{caseData.case_type}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Solicitor</p>
              <p className="text-sm font-medium text-slate-700">{caseData.solicitor_name}</p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">{completedCount} of {stages.length} milestones complete</p>
        </div>

        {/* Milestones */}
        <div className="space-y-3" data-testid="referrer-milestones">
          {stages.map((stage, idx) => {
            const isCompleted = stage.status === "completed";
            const isCurrent = stage.status === "current";
            const subsCompleted = (stage.subcategories || []).filter((s) => s.completed).length;
            const subsTotal = (stage.subcategories || []).length;
            return (
              <div key={stage.key} className={`bg-white border rounded-lg p-4 transition-colors ${
                isCompleted ? "border-emerald-200 bg-emerald-50/30" : isCurrent ? "border-blue-200 bg-blue-50/20" : "border-slate-200"
              }`} data-testid={`milestone-${stage.key}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    isCompleted ? "bg-emerald-600" : isCurrent ? "bg-blue-600" : "bg-slate-200"
                  }`}>
                    {isCompleted ? (
                      <Check weight="bold" className="w-4 h-4 text-white" />
                    ) : isCurrent ? (
                      <Clock weight="bold" className="w-4 h-4 text-white" />
                    ) : (
                      <Circle weight="bold" className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold ${isCompleted ? "text-emerald-800" : isCurrent ? "text-blue-800" : "text-slate-500"}`}>
                        {stage.label}
                      </p>
                      {isCompleted && stage.completed_at && (
                        <span className="text-[10px] text-emerald-600 font-medium">
                          {new Date(stage.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </span>
                      )}
                      {isCurrent && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">In Progress</span>}
                    </div>
                    {stage.notes && <p className="text-xs text-slate-500 mt-0.5">{stage.notes}</p>}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {subsTotal > 0 && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        subsCompleted === subsTotal ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {subsCompleted}/{subsTotal}
                      </span>
                    )}
                  </div>
                </div>

                {/* Sub-tasks */}
                {subsTotal > 0 && (
                  <div className="mt-3 pl-11 space-y-1">
                    {stage.subcategories.map((sub) => (
                      <div key={sub.id} className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                          sub.completed ? "bg-emerald-600 border-emerald-600" : "border-slate-300"
                        }`}>
                          {sub.completed && <Check weight="bold" className="w-2 h-2 text-white" />}
                        </div>
                        <span className={`text-xs ${sub.completed ? "text-slate-400 line-through" : "text-slate-600"}`}>
                          {sub.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-8">
          This is a read-only view of case milestones. For full details, please contact the solicitor.
        </p>
      </div>
    </div>
  );
}
